import { logger } from '@/utils/logger';

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  context?: Record<string, any>;
}

interface LoadingState {
  isLoading: boolean;
  progress?: number;
  stage?: string;
  error?: string;
}

class PerformanceMonitoringService {
  private metrics: PerformanceMetric[] = [];
  private loadingStates: Map<string, LoadingState> = new Map();
  private observers: Map<string, PerformanceObserver> = new Map();

  init() {
    this.setupPerformanceObservers();
    this.monitorResourceLoading();
  }

  private setupPerformanceObservers() {
    if ('PerformanceObserver' in window) {
      // Monitor navigation timing
      const navObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          this.recordMetric('navigation', entry.duration, {
            type: entry.entryType,
            name: entry.name
          });
        });
      });
      navObserver.observe({ entryTypes: ['navigation'] });
      this.observers.set('navigation', navObserver);

      // Monitor largest contentful paint
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        this.recordMetric('lcp', lastEntry.startTime, {
          element: lastEntry.element?.tagName
        });
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      this.observers.set('lcp', lcpObserver);

      // Monitor cumulative layout shift
      const clsObserver = new PerformanceObserver((list) => {
        let clsValue = 0;
        for (const entry of list.getEntries()) {
          const layoutShiftEntry = entry as any;
          if (!layoutShiftEntry.hadRecentInput) {
            clsValue += layoutShiftEntry.value;
          }
        }
        this.recordMetric('cls', clsValue);
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
      this.observers.set('cls', clsObserver);
    }
  }

  private monitorResourceLoading() {
    // Monitor script loading times
    const originalAppendChild = Node.prototype.appendChild;
    Node.prototype.appendChild = function(child: any) {
      if (child.tagName === 'SCRIPT' && child.src) {
        const startTime = performance.now();
        child.onload = () => {
          const loadTime = performance.now() - startTime;
          performanceService.recordMetric('script-load', loadTime, {
            src: child.src
          });
        };
      }
      return originalAppendChild.call(this, child);
    };
  }

  recordMetric(name: string, value: number, context?: Record<string, any>) {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      context
    };

    this.metrics.push(metric);
    
    // Keep only last 100 metrics to prevent memory issues
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }

    console.log(`Performance: ${name}`, { value, context });
  }

  setLoadingState(key: string, state: LoadingState) {
    this.loadingStates.set(key, state);
    
    if (state.error) {
      logger.error(`Loading error for ${key}:`, state.error);
    }
  }

  getLoadingState(key: string): LoadingState | undefined {
    return this.loadingStates.get(key);
  }

  clearLoadingState(key: string) {
    this.loadingStates.delete(key);
  }

  getMetrics(name?: string): PerformanceMetric[] {
    if (name) {
      return this.metrics.filter(m => m.name === name);
    }
    return [...this.metrics];
  }

  getAverageMetric(name: string): number {
    const metrics = this.getMetrics(name);
    if (metrics.length === 0) return 0;
    
    const sum = metrics.reduce((acc, m) => acc + m.value, 0);
    return sum / metrics.length;
  }

  generatePerformanceReport() {
    return {
      averageLCP: this.getAverageMetric('lcp'),
      averageCLS: this.getAverageMetric('cls'),
      navigationTime: this.getAverageMetric('navigation'),
      scriptLoadTimes: this.getMetrics('script-load'),
      totalMetrics: this.metrics.length,
      activeLoadingStates: this.loadingStates.size,
      timestamp: Date.now()
    };
  }

  cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
    this.metrics.length = 0;
    this.loadingStates.clear();
  }
}

export const performanceService = new PerformanceMonitoringService();
export type { PerformanceMetric, LoadingState };
